import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://healthtravelturkiye.com';

interface Contract {
  id: string;
  title: string;
  version: string;
  updated_at: string;
  content: string;
  contract_type: string;
}

export default function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  useEffect(() => {
    const tc = i18n.getFixedT(i18n.language, 'common');
    document.title = tc('seo_privacy_title');
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', tc('seo_privacy_desc'));
    setMeta('keywords', 'gizlilik politikası, kişisel veri güvenliği, veri koruma, çerez politikası, KVKK');
    setMeta('robots', 'index, follow');
    setMeta('og:title', tc('seo_privacy_title'), true);
    setMeta('og:description', tc('seo_privacy_desc'), true);
    setMeta('og:url', `${SITE_URL}/gizlilik-politikasi`, true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/2b1eaec775bcbf02db3915a6b5c2ab74.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=data%20privacy%20security%20shield%20protection%20personal%20information%20digital%20lock%20clean%20minimal%20professional%20design%20teal%20white%20background%20modern&width=1200&height=630&seq=og-privacy-v1&orientation=landscape');

    const scriptId = 'schema-privacy';
    let script = document.getElementById(scriptId);
    if (!script) { script = document.createElement('script'); script.id = scriptId; (script as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: tc('seo_privacy_title'),
      description: tc('seo_privacy_desc'),
      url: `${SITE_URL}/gizlilik-politikasi`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Gizlilik Politikası', item: `${SITE_URL}/gizlilik-politikasi` },
        ],
      },
    });
    return () => { document.getElementById(scriptId)?.remove(); };
  }, [i18n.language]);

  const fetchPrivacyPolicy = async () => {
    try {
      const { data, error } = await supabase
        .from('contracts')
        .select('*')
        .eq('contract_type', 'privacy_policy')
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setContract(data);
      }
    } catch (error) {
      console.error('Gizlilik politikası yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const lastUpdated = contract?.updated_at
    ? new Date(contract.updated_at).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '15 Ocak 2025';

  const version = contract?.version || 'v1.0';
  const title = contract?.title || t('privacy_title');
  const content = contract?.content || '';

  // Fallback statik içerik
  const fallbackSections = [
    {
      title: '1. Toplanan Veriler',
      icon: 'ri-database-2-line',
      content: `Platformumuza kayıt olurken ve kullanırken aşağıdaki kişisel verileriniz toplanmaktadır:`,
      list: [
        'Kimlik bilgileri: Ad, soyad, T.C. kimlik numarası (kurumlar için)',
        'İletişim bilgileri: E-posta adresi, telefon numarası, adres',
        'Hesap bilgileri: Kullanıcı adı, şifre (şifrelenmiş olarak)',
        'Sağlık bilgileri: Görüşme talebi formunda paylaşılan bilgiler',
        'Kullanım verileri: Sayfa ziyaretleri, tıklama verileri, oturum süreleri',
        'Cihaz bilgileri: IP adresi, tarayıcı türü, işletim sistemi',
      ],
    },
    {
      title: '2. Verilerin Kullanım Amaçları',
      icon: 'ri-shield-check-line',
      content: `Toplanan kişisel veriler aşağıdaki amaçlarla kullanılmaktadır:`,
      list: [
        'Üyelik hesabınızın oluşturulması ve yönetilmesi',
        'Görüşme taleplerinin ilgili sağlık kurumuna iletilmesi',
        'Platform hizmetlerinin iyileştirilmesi ve kişiselleştirilmesi',
        'Yasal yükümlülüklerin yerine getirilmesi',
        'İstatistiksel analizler ve raporlama (anonim olarak)',
        'Güvenlik önlemlerinin sağlanması ve dolandırıcılığın önlenmesi',
      ],
    },
    {
      title: '3. Verilerin Paylaşımı',
      icon: 'ri-share-line',
      content: `Kişisel verileriniz aşağıdaki durumlar dışında üçüncü taraflarla paylaşılmaz:`,
      list: [
        'Görüşme talebi gönderdiğiniz sağlık kurumu ile (yalnızca talep bilgileri)',
        'Yasal zorunluluk halinde yetkili kamu kurumları ile',
        'Platformun teknik altyapısını sağlayan hizmet sağlayıcılar ile (veri işleme sözleşmesi kapsamında)',
        'Açık rızanızın bulunduğu diğer durumlar',
      ],
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white font-['Outfit']">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]" style={{ paddingTop: 'var(--navbar-height)' }}>
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">{t('legal_loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Outfit']">
      <Navbar />

      <div style={{ paddingTop: 'var(--navbar-height)' }}>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">{title}</h1>
            <p className="text-base text-white/70">
              {t('legal_last_updated')}: {lastUpdated} &middot; {t('legal_version')}: {version}
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 mb-10">
            <div className="flex items-start space-x-3">
              <i className="ri-shield-check-line text-teal-500 text-lg mt-0.5 flex-shrink-0"></i>
              <p className="text-sm text-teal-800 leading-relaxed">
                {t('privacy_info_text')}
              </p>
            </div>
          </div>

          {content ? (
            <div
              className="prose prose-sm max-w-none text-gray-700 leading-relaxed mb-10"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <div className="space-y-10">
              {fallbackSections.map((section, index) => (
                <article key={index} className="group">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-xl flex-shrink-0">
                      <i className={`${section.icon} text-gray-600 text-lg`}></i>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">
                      {section.title}
                    </h2>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 pl-[52px]">
                    {section.content}
                  </p>
                  {section.list.length > 0 && (
                    <ul className="space-y-2 pl-[52px]">
                      {section.list.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <i className="ri-checkbox-circle-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                          <span className="text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {index < fallbackSections.length - 1 && (
                    <div className="mt-10 border-b border-gray-100"></div>
                  )}
                </article>
              ))}
            </div>
          )}

          <div className="mt-16 p-8 bg-gray-50 rounded-2xl text-center">
            <p className="text-sm text-gray-600 mb-4">
              {t('legal_contact_desc')}
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center px-6 py-3 bg-rose-500 text-white rounded-xl font-semibold hover:bg-rose-600 transition-all text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              {t('legal_contact_btn')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}